import SelectField from "$components/inputs/SelectField";
import { TextField } from "$components/rhf-fields";
import type { Territory } from "$global-providers/config-provider/provider";
import {
  type AddressLine,
  addressLine1,
  addressLine2,
  addressLine3,
  addressLine4,
} from "$routes/_lib/address-lines";
import { TERRITORY } from "$routes/_lib/constants";
import { useLocalitiesContext } from "$routes/_lib/localities-context";
import React from "react";
import { useController, useFormContext } from "react-hook-form";
import { useIntl } from "react-intl";

const AddressSelectField: FC<
  { options: string[]; freeText?: boolean; onNewValue?: () => void } & Omit<
    React.ComponentProps<typeof SelectField>,
    "options" | "onChange"
  >
> = React.memo(({ name, options, freeText, onNewValue, ...restProps }) => {
  const controller = useController({ name });
  const optionObjs = React.useMemo(
    () => options.sort().map((option) => ({ label: option, value: option })),
    [options],
  );

  if (freeText) {
    return (
      <TextField
        name={name}
        label={restProps.label}
        required={restProps.required}
        fullWidth={restProps.fullWidth}
      />
    );
  }

  return (
    <SelectField
      {...controller.field}
      id={name}
      value={controller.field.value ?? ""}
      onChange={(event, ...rest) => {
        controller.field.onChange(event, ...rest);
        onNewValue?.();
      }}
      error={controller.fieldState.error?.message}
      disabled={options.length < 1}
      options={optionObjs}
      {...restProps}
    />
  );
});

export const RHFAddressFields: FC<{
  fieldNames: {
    country: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    addressLine4: string;
  };
  required?: boolean;
  territory: Territory;
}> = ({ fieldNames, required, territory }) => {
  const { localities, error: errorFetchingLocalities } = useLocalitiesContext();

  const { formatMessage } = useIntl();

  const { watch, setValue, resetField } = useFormContext();

  const line1 = watch(fieldNames.addressLine1);
  const line2 = watch(fieldNames.addressLine2);
  const line3 = watch(fieldNames.addressLine3);

  const level1Localities = Object.keys(localities);
  if (level1Localities.length === 1 && !line1) {
    setValue(fieldNames.addressLine2, level1Localities[0]);
  }

  const level2Localities = React.useMemo(() => {
    return Object.keys(localities?.[line1] ?? []);
  }, [line1, localities]);
  if (level2Localities.length === 1 && !line2) {
    setValue(fieldNames.addressLine2, level2Localities[0]);
  }

  const level3Localities = React.useMemo(() => {
    return localities?.[line1]?.[line2] ?? [];
  }, [line1, line2, localities]);
  if (level3Localities.length === 1 && !line3) {
    setValue(fieldNames.addressLine3, level3Localities[0]);
  }

  // Sometimes territory can be undefined. In these cases, the default territory will be Kenya.
  const territoryLabel = (addressLine: AddressLine) =>
    formatMessage(addressLine[territory] || addressLine[TERRITORY.KENYA]);

  return (
    <>
      <TextField
        style={{ display: "none" }}
        name={fieldNames.country}
        type="hidden"
        data-testid="addressCountryField"
        required
        fullWidth
      />
      <AddressSelectField
        options={level1Localities}
        name={fieldNames.addressLine1}
        label={territoryLabel(addressLine1)}
        required={required}
        freeText={errorFetchingLocalities}
        fullWidth
        onNewValue={() => {
          resetField(fieldNames.addressLine2);
          resetField(fieldNames.addressLine3);
        }}
      />
      <AddressSelectField
        options={level2Localities}
        name={fieldNames.addressLine2}
        label={territoryLabel(addressLine2)}
        freeText={errorFetchingLocalities || territory === TERRITORY.LIBERIA}
        fullWidth
        onNewValue={() => resetField(fieldNames.addressLine3)}
        required={required ? territory === TERRITORY.PAKISTAN : false}
      />
      <AddressSelectField
        options={level3Localities}
        name={fieldNames.addressLine3}
        label={territoryLabel(addressLine3)}
        freeText={errorFetchingLocalities || territory !== TERRITORY.KENYA}
        required={required ? territory === TERRITORY.NIGERIA : false}
        fullWidth
      />
      <TextField
        data-testid={fieldNames.addressLine4}
        name={fieldNames.addressLine4}
        label={territoryLabel(addressLine4)}
        required={required}
        fullWidth
      />
    </>
  );
};
